import { useState } from 'react'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Card } from '@magnetic/card'
import { Container } from '@magnetic/container'
import { Flex } from '@magnetic/flex'
import { Tabs } from '@magnetic/tabs'
import {
  Database,
  ShieldCheck,
  Trash,
  ChartBar,
  WarningCircle
} from '@/lib/icons'
import { ECROverview } from '@/components/ecr-overview'
import { ECRRepositoriesTable } from '@/components/ecr-repositories-table'
import { ECRVulnerableImages } from '@/components/ecr-vulnerable-images'
import { ECRCleanupTool } from '@/components/ecr-cleanup-tool'
import { listApplicationsWithIssues, listECRRepositories } from '@/lib/api'

type Tab = 'overview' | 'repositories' | 'security' | 'cleanup'

interface TabConfig {
  id: Tab
  label: string
  icon: React.ReactNode
}

export function ECRAdminPage() {
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const { data: issuesResponse } = useConnectQuery(
    listApplicationsWithIssues,
    {},
    { refetchInterval: 60000 }
  )
  const ecrIssues = (issuesResponse?.applications || []).filter((issue) => issue.issueType === 'ecr_error')

  const tabs: TabConfig[] = [
    { id: 'overview', label: 'Overview', icon: <ChartBar size={16} weight="bold" /> },
    { id: 'repositories', label: 'Repositories', icon: <Database size={16} weight="bold" /> },
    { id: 'security', label: 'Security', icon: <ShieldCheck size={16} weight="bold" /> },
    { id: 'cleanup', label: 'Cleanup', icon: <Trash size={16} weight="bold" /> },
  ]

  return (
    <Flex direction="vertical" gap={24}>
      {/* Header */}
      <Flex direction="vertical" gap={4}>
        <Heading size="h1">ECR Registry Management</Heading>
        <Text color="light">
          Manage AWS ECR container images, security vulnerabilities, and storage optimization
        </Text>
      </Flex>

      {/* Tabs */}
      <Tabs>
        {tabs.map((tab) => (
          <Tabs.Link
            key={tab.id}
            selected={activeTab === tab.id}
            href={`#${tab.id}`}
            onClick={(e) => {
              e.preventDefault()
              setActiveTab(tab.id)
            }}
          >
            {tab.label}
          </Tabs.Link>
        ))}
      </Tabs>

      {/* Content */}
      <Container>
        <Flex direction="vertical" gap={24}>
          {ecrIssues.length > 0 && (
            <Card>
              <Flex direction="vertical" gap="sm" style={{ padding: 16 }}>
                <Flex align="center" gap="sm">
                  <WarningCircle size={20} />
                  <Heading size="sub-section">ECR deployment failures</Heading>
                </Flex>
                <Text size="p3" color="light">
                  {ecrIssues.length} application{ecrIssues.length === 1 ? '' : 's'} failed to push images to ECR. Please retry or contact support.
                </Text>
                <Flex direction="vertical" gap="xs">
                  {ecrIssues.slice(0, 3).map((issue) => (
                    <Text key={issue.application?.id} size="p4">
                      {issue.application?.displayName || issue.application?.name} — {issue.issueDescription || 'ECR deployment failed'}
                    </Text>
                  ))}
                  {ecrIssues.length > 3 && (
                    <Text size="p4" color="light">And {ecrIssues.length - 3} more…</Text>
                  )}
                </Flex>
              </Flex>
            </Card>
          )}

          {activeTab === 'overview' && (
            <Flex direction="vertical" gap={24}>
              <ECROverview />
              <Card.Group>
                <Card>
                  <Flex direction="vertical" gap={12}>
                    <Heading size="section">Quick stats</Heading>
                    <Flex direction="vertical" gap={8}>
                      <Flex direction="horizontal" justify="space-between">
                        <Text size="p3" color="light">Active Deployments</Text>
                        <Text size="p3" weight="semi-bold" monospace>-</Text>
                      </Flex>
                      <Flex direction="horizontal" justify="space-between">
                        <Text size="p3" color="light">Unused Images (30+ days)</Text>
                        <Text size="p3" weight="semi-bold" monospace>-</Text>
                      </Flex>
                      <Flex direction="horizontal" justify="space-between">
                        <Text size="p3" color="light">Storage Growth (30d)</Text>
                        <Text size="p3" weight="semi-bold" monospace>-</Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </Card>
                <Card>
                  <Flex direction="vertical" gap={12}>
                    <Heading size="section">Recommendations</Heading>
                    <Flex direction="vertical" gap={8}>
                      <Text size="p3" color="light">• Run cleanup to remove unused images</Text>
                      <Text size="p3" color="light">• Review critical vulnerabilities</Text>
                      <Text size="p3" color="light">• Enable lifecycle policies</Text>
                      <Text size="p3" color="light">• Monitor storage growth trends</Text>
                    </Flex>
                  </Flex>
                </Card>
              </Card.Group>
            </Flex>
          )}

          {activeTab === 'repositories' && (
            <ECRRepositoriesTable
              queryFn={listECRRepositories}
              title="ECR Repositories"
              description="Browse and manage all ECR container repositories"
              selectableRows={false}
              initialPageSize={20}
            />
          )}

          {activeTab === 'security' && <ECRVulnerableImages />}

          {activeTab === 'cleanup' && <ECRCleanupTool />}
        </Flex>
      </Container>
    </Flex>
  )
}
